import React, { useState, useCallback, useEffect } from "react";
import { createPortal } from "react-dom";
import Cropper from "react-easy-crop";

async function getCroppedImg(imageSrc, pixelCrop, rotation = 0) {
  const image = await new Promise((resolve, reject) => {
    const img = new Image();
    img.addEventListener("load", () => resolve(img));
    img.addEventListener("error", (err) => reject(err));
    img.src = imageSrc;
  });

  const rad = (rotation * Math.PI) / 180;
  const { naturalWidth: iw, naturalHeight: ih } = image;
  const bboxW = Math.round(Math.abs(iw * Math.cos(rad)) + Math.abs(ih * Math.sin(rad)));
  const bboxH = Math.round(Math.abs(iw * Math.sin(rad)) + Math.abs(ih * Math.cos(rad)));

  const rotCanvas = document.createElement("canvas");
  rotCanvas.width  = bboxW;
  rotCanvas.height = bboxH;
  const rotCtx = rotCanvas.getContext("2d");
  rotCtx.translate(bboxW / 2, bboxH / 2);
  rotCtx.rotate(rad);
  rotCtx.drawImage(image, -iw / 2, -ih / 2);

  const cropCanvas = document.createElement("canvas");
  cropCanvas.width  = pixelCrop.width;
  cropCanvas.height = pixelCrop.height;
  const cropCtx = cropCanvas.getContext("2d");
  cropCtx.drawImage(
    rotCanvas,
    pixelCrop.x, pixelCrop.y,
    pixelCrop.width, pixelCrop.height,
    0, 0,
    pixelCrop.width, pixelCrop.height
  );

  return new Promise((resolve) => {
    cropCanvas.toBlob(
      (blob) => resolve({ blob, dataUrl: cropCanvas.toDataURL("image/jpeg", 0.92) }),
      "image/jpeg", 0.92
    );
  });
}

/* ─────────────────────────────────────────
   Parse frame size string → aspect ratio
   "18x12" → 18/12 = 1.5
   "12x8"  → 12/8  = 1.5
   "8x12"  → 8/12  = 0.667
───────────────────────────────────────── */
const parseFrameAspect = (sizeStr, orientation = "portrait") => {
  if (!sizeStr) return null;
  const match = String(sizeStr).match(/(\d+(?:\.\d+)?)\s*[xX*]\s*(\d+(?:\.\d+)?)/);
  if (!match) return null;
  let w = parseFloat(match[1]);
  let h = parseFloat(match[2]);
  if (!w || !h) return null;

  if (orientation === "landscape") {
    [w, h] = [h, w];
  }

  return { w, h, ratio: w / h, label: `${w}:${h}` };
};

/* ─────────────────────────────────────────
   Component
───────────────────────────────────────── */
const ImageCropModal = ({ imageSrc, onDone, onCancel, frameSize,orientation = "portrait" }) => {
  const [crop, setCrop]                           = useState({ x: 0, y: 0 });
  const [zoom, setZoom]                           = useState(1);
  const [rotation, setRotation]                   = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing]               = useState(false);
  const [selectedPreset, setSelectedPreset]       = useState("Free");
  const [aspect, setAspect]                       = useState(undefined); // Free by default
  const parsedFrame = parseFrameAspect(frameSize, orientation);

  // Build dynamic presets: [FrameSize, Free]
  const ASPECT_PRESETS = [
    ...(parsedFrame
      ? [{ label: parsedFrame.label, value: parsedFrame.ratio }]
      : []),
    { label: "Free", value: null },
  ];

useEffect(() => {
  if (parsedFrame) {
    setSelectedPreset(parsedFrame.label);
    setAspect(parsedFrame.ratio);
  } else {
    setSelectedPreset("Free");
    setAspect(undefined);
  }
}, [frameSize, orientation]);
  const onCropComplete = useCallback((_, pixels) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handlePresetClick = (preset) => {
    setSelectedPreset(preset.label);
    setAspect(preset.label === "Free" ? undefined : preset.value);
  };

  const rotateLeft  = () => setRotation((r) => r - 90);
  const rotateRight = () => setRotation((r) => r + 90);

  const handleDone = async () => {
    if (!croppedAreaPixels) return;
    setProcessing(true);
    try {
      const { blob, dataUrl } = await getCroppedImg(imageSrc, croppedAreaPixels, rotation);
      const file = new File([blob], "cropped-image.jpg", { type: "image/jpeg" });
      onDone({ file, dataUrl });
    } catch (e) {
      console.error(e);
    } finally {
      setProcessing(false);
    }
  };

  const presetBtn = (active) => ({
    padding: "5px 18px", borderRadius: "20px", fontSize: "14px",
    border: `1.5px solid ${active ? "#f97316" : "#d1d5db"}`,
    background: active ? "#fff7ed" : "#fff",
    color: active ? "#f97316" : "#374151",
    cursor: "pointer", fontWeight: active ? 600 : 400,
  });

  const rotBtn = {
    width: "38px", height: "38px", borderRadius: "8px",
    border: "1.5px solid #d1d5db", background: "#fff",
    cursor: "pointer", fontSize: "18px", display: "flex",
    alignItems: "center", justifyContent: "center",
    color: "#374151", flexShrink: 0,
  };

  return createPortal(
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 99999,
        background: "rgba(0,0,0,0.85)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "16px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div
        style={{
          background: "#fff", borderRadius: "16px",
          width: "100%", maxWidth: "700px",
          boxShadow: "0 24px 64px rgba(0,0,0,0.4)",
          overflow: "hidden",
          maxHeight: "95vh", overflowY: "auto",
        }}
      >
        {/* ── Header ── */}
        <div style={{
          padding: "14px 20px", borderBottom: "1px solid #eee",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <span style={{
            padding: "4px 14px", borderRadius: "20px", fontSize: "13px",
            background: "#f97316", color: "#fff", fontWeight: 600,
          }}>
            Crop & Rotate
          </span>

          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            {/* ✅ Use This Image — top right */}
         

            <button onClick={onCancel} style={{
              background: "none", border: "none", fontSize: "40px",
              cursor: "pointer", color: "red", lineHeight: 1,
            }}>×</button>
          </div>
        </div>

        {/* ── Crop area ── */}
        <div style={{ position: "relative", height: "380px", background: "#111" }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
          />
        </div>

        {/* ── Controls ── */}
        <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: "14px" }}>

          {/* ✅ Only frame size + Free shown */}
          <div style={{position:"relative"}}>

              <div className="desktopNone">
            <button
              onClick={handleDone}
              disabled={processing}
            
              style={{
                padding: "10px 28px", borderRadius: "8px",
                background: "#f97316", color: "#fff",
                border: "none", cursor: "pointer",
                fontSize: "14px", fontWeight: 600,
                opacity: processing ? 0.7 : 1,
              }}
            >
              {processing ? "Saving..." : "✓ Use This Image"}
            </button></div>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: 500, display: "block", marginBottom: "8px" }}>
              Crop Size / Ratio
              {parsedFrame && (
                <span className="mobileNone" style={{ marginLeft: "8px", color: "#9ca3af", fontWeight: 400 }}>
                  (matched from frame size: {frameSize})
                </span>
              )}
            </label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
              {ASPECT_PRESETS.map((p) => (
                <button key={p.label} onClick={() => handlePresetClick(p)} style={presetBtn(selectedPreset === p.label)}>
                  {p.label}
                </button>
              ))}
            </div>


               <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end",position:"absolute",top:"10px",right:"10px" }}>
          
            <button
              onClick={handleDone}
              disabled={processing}
              className="mobileNone"
              style={{
                padding: "10px 28px", borderRadius: "8px",
                background: "#f97316", color: "#fff",
                border: "none", cursor: "pointer",
                fontSize: "14px", fontWeight: 600,
                opacity: processing ? 0.7 : 1,
              }}
            >
              {processing ? "Saving..." : "✓ Use This Image"}
            </button>
          </div>
          </div>

          {/* Zoom */}
          <div>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: 500 }}>
              Zoom: {zoom.toFixed(1)}×
            </label>
            <input
              type="range" min={1} max={3} step={0.05} value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{ width: "100%", accentColor: "#f97316" }}
            />
          </div>

          {/* Rotation */}
          <div>
            <label style={{ fontSize: "13px", color: "#374151", fontWeight: 500, display: "block", marginBottom: "8px" }}>
              Rotation: {rotation}°
            </label>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <button onClick={rotateLeft} title="Rotate left 90°" style={rotBtn}>↺</button>
              <input
                type="range" min={-180} max={180} step={1} value={rotation}
                onChange={(e) => setRotation(Number(e.target.value))}
                style={{ flex: 1, accentColor: "#f97316" }}
              />
              <button onClick={rotateRight} title="Rotate right 90°" style={rotBtn}>↻</button>
            </div>
          </div>

       
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageCropModal;